"use client";

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useRouter } from "next/navigation";

import { Input } from "../project_components/general/input";
import { Button } from "../project_components/general/button";
import { Label } from "../project_components/general/label";
import GoogleOAuthButton from "./googleAuthBtt";
import { CheckSquareIcon } from "lucide-react";

interface LoginFormProps {
  onViewChange?: (view: "login" | "reset-password" | "reset-sent") => void;
}

export default function LoginForm({ onViewChange }: LoginFormProps) {
  const router = useRouter();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetSentEmail, setResetSentEmail] = useState("");

  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);

  const [loginErrors, setLoginErrors] = useState<{
    emailAddress?: string[];
    password?: string[];
  } | null>(null);
  const [resetErrors, setResetErrors] = useState<{
    emailAddress?: string[];
  } | null>(null);

  // 로그인 처리
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoginLoading(true);
    setLoginErrors(null);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailAddress: email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          setLoginErrors(data.errors);
        }
        if (data.message) {
          toast.error(data.message);
        }
        setIsLoginLoading(false);
        return;
      }

      // 성공 시 리디렉션 - 로딩 상태 유지
      if (data.redirectUrl) {
        router.push(data.redirectUrl);
        // 로딩 상태를 유지하여 사용자가 다시 클릭하지 못하도록 함
        // 페이지 전환이 완료되면 컴포넌트가 언마운트되므로 setIsLoginLoading(false) 불필요
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("로그인 중 오류가 발생했습니다.");
      setIsLoginLoading(false);
    }
  };

  // 비밀번호 재설정 처리
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetLoading(true);
    setResetErrors(null);

    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailAddress: resetEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          setResetErrors(data.errors);
        }
        if (data.message) {
          toast.error(data.message);
        }
        return;
      }

      // 성공 시
      if (data.success && data.email) {
        setResetEmailSent(true);
        setResetSentEmail(data.email);
        onViewChange?.("reset-sent");
        toast.info(
          data.message || "비밀번호 재설정 링크가 발송되었습니다."
        );
      }
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error("비밀번호 재설정 중 오류가 발생했습니다.");
    } finally {
      setIsResetLoading(false);
    }
  };

  // Google 로그인
  const handleGoogleLoginClick = () => {
    window.location.href = "/api/auth/google";
  };

  // 비밀번호 재설정 이메일 발송 완료 화면
  if (resetEmailSent) {
    return (
      <div className="w-full space-y-6 text-center">
        <div className="flex justify-center mb-4">
          <CheckSquareIcon className="h-12 w-12 text-green-500" />
        </div>
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong className="text-black dark:text-white">
              {resetSentEmail}
            </strong>
            으로
            <br />
            비밀번호 재설정 링크를 발송드렸습니다.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            메일함(스팸함 포함)을 확인하고 링크를 클릭해주세요.
          </p>
        </div>
        <Button
          onClick={() => {
            // 페이지 새로고침으로 모든 상태 초기화
            window.location.href = "/login";
          }}
          variant="outline"
          className="w-full"
        >
          로그인으로 돌아가기
        </Button>
      </div>
    );
  }

  // 비밀번호 재설정 화면
  if (showResetPassword) {
    return (
      <div className="w-full space-y-4">
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div>
            <Label htmlFor="resetEmailAddress">이메일 주소</Label>
            <Input
              id="resetEmailAddress"
              name="emailAddress"
              type="email"
              placeholder="이메일 주소를 입력하세요"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              autoComplete="email"
              aria-describedby="reset-email-error"
            />
            {resetErrors?.emailAddress && (
              <p id="reset-email-error" className="text-sm text-red-500 mt-1">
                {resetErrors.emailAddress[0]}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isResetLoading}
          >
            {isResetLoading ? "전송 중..." : "비밀번호 재설정 메일 보내기"}
          </Button>
        </form>

        <Button
          onClick={() => {
            setShowResetPassword(false);
            setResetEmail("");
            onViewChange?.("login");
          }}
          variant="outline"
          className="w-full"
        >
          돌아가기
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <form onSubmit={handleLogin} className="flex flex-col w-full gap-4">
        <div>
          <Label htmlFor="emailAddress">ID (이메일)</Label>
          <Input
            id="emailAddress"
            name="emailAddress"
            type="email"
            placeholder="이메일(ID)을 입력하세요"
            required
            aria-describedby="email-error"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          {loginErrors?.emailAddress && (
            <p id="email-error" className="text-sm text-red-500 mt-1">
              {loginErrors.emailAddress[0]}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="password">비밀번호</Label>
          <Input
            id="password"
            name="password"
            type="password"
            placeholder="비밀번호를 입력하세요 (최소 8자)"
            required
            autoComplete="current-password"
            aria-describedby="password-error"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {loginErrors?.password && (
            <p id="password-error" className="text-sm text-red-500 mt-1">
              {loginErrors.password[0]}
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              setShowResetPassword(true);
              onViewChange?.("reset-password");
            }}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            비밀번호를 잊어버리셨나요?
          </button>
        </div>

        <Button
          className="w-full mt-5"
          type="submit"
          disabled={isLoginLoading}
        >
          {isLoginLoading ? "로그인 중..." : "Login with Password"}
        </Button>
      </form>

      {/* <div className="flex items-center space-x-2 my-4">
        <hr className="flex-grow border-gray-300" />
        <span className="text-sm text-gray-500">OR</span>
        <hr className="flex-grow border-gray-300" />
      </div>

      <div className="w-full cursor-pointer">
        <GoogleOAuthButton onClick={handleGoogleLoginClick} />
      </div> */}
    </div>
  );
}
